// BYO-01 "My subscriptions": tool connections and the subscription policy a company reads.
// Provider rules: Work OS `connect` (lifted in `packages/domain-agents/src/connect`).
import { z } from "zod";
import { ActorShortColumns } from "./actor";
import { ModelCredentialPolicy } from "./de-config-rows";
import {
  CrewConnectionKind,
  ToolConnectionProvider,
  ToolConnectionScope,
  ToolConnectionState,
} from "./enums";
import { Timestamp, Uuid } from "./primitives";

const stamps = { created_at: Timestamp, updated_at: Timestamp };

/**
 * A custom API key's declaration (Work OS `ApiKeyConfig`, review findings 21–22): one public
 * host, where the key goes, and the read-only path a check calls.
 */
export const ToolConnectionConfig = z
  .strictObject({
    name: z
      .string()
      .max(40)
      .regex(/^[A-Z0-9_]{2,40}$/),
    host: z.string().min(3).max(253),
    position: z.discriminatedUnion("in", [
      z.strictObject({
        in: z.literal("header"),
        name: z.string().min(1).max(64),
        scheme: z.string().max(21).nullable().optional(),
      }),
      z.strictObject({ in: z.literal("query"), name: z.string().min(1).max(64) }),
    ]),
    probe_path: z.string().min(1).max(1024),
  })
  .meta({ id: "ToolConnectionConfig" });
export type ToolConnectionConfig = z.infer<typeof ToolConnectionConfig>;

/** A connection as every reader sees it: the sealed secret never leaves the server. */
export const ToolConnection = z
  .object({
    id: Uuid,
    scope: ToolConnectionScope,
    company_id: Uuid,
    owner_user_id: Uuid.nullable(),
    provider: ToolConnectionProvider,
    label: z.string(),
    config: ToolConnectionConfig.nullable(),
    tiers: z.array(z.string()),
    identity_display: z.string().nullable(),
    key_last4: z.string().nullable(),
    state: ToolConnectionState,
    last_error_class: z.string().nullable(),
    last_verified_at: Timestamp.nullable(),
    last_checked_at: Timestamp.nullable(),
    // M4-04: refresh and health (Work OS `integration_accounts`).
    /** OAuth: when the access token expires; CompanyOS refreshes it five minutes before. */
    token_expires_at: Timestamp.nullable(),
    last_refreshed_at: Timestamp.nullable(),
    /** While rate-limited: when calls may resume. */
    rate_limited_until: Timestamp.nullable(),
    created_by_user_id: Uuid,
    revoked_at: Timestamp.nullable(),
    revoked_by_user_id: Uuid.nullable(),
    ...stamps,
  })
  .meta({ id: "ToolConnection" });
export type ToolConnection = z.infer<typeof ToolConnection>;

/** One entry of the connection catalogue (Work OS `catalogue.ts`, reduced to what a card shows). */
export const ConnectionProvider = z
  .object({
    key: ToolConnectionProvider,
    kind: z.enum(["oauth", "api_key"]),
    name: z.string(),
    summary: z.string(),
    /** OAuth: the server holds the founder's OAuth app credentials for it. */
    configured: z.boolean(),
    /** The environment variables an admin sets up (never their values). */
    setup_env: z.array(z.string()),
    tiers: z.array(
      z.object({
        key: z.string(),
        label: z.string(),
        gives: z.string(),
        does_not: z.string(),
        note: z.string().nullable(),
        /** Offered only after a founder gate (Gmail read: Google's security review). */
        founder_gated: z.boolean(),
      }),
    ),
    always_not: z.array(z.string()),
  })
  .meta({ id: "ConnectionProvider" });
export type ConnectionProvider = z.infer<typeof ConnectionProvider>;

/**
 * The company flags of "My subscriptions" (`company.settings.flags`, set through
 * `PATCH /v1/companies/{co}` by owners and admins). Each path also needs its platform switch in
 * `ModelCredentialPolicy` (the founder's, OPEN-QUESTIONS 46–50); a missing flag is off.
 */
export const SUBSCRIPTION_FLAGS = {
  modelApiKeys: "byo_model_provider_keys",
  modelCliSubscriptions: "byo_model_cli_subscriptions",
  toolConnections: "byo_tool_connections",
  /** M4-04: crews may read their owners' mail and calendar (with tool connections on). */
  crewMailCalendar: "crew_mail_calendar",
} as const;

export const CompanySubscriptionFlags = z
  .object({
    model_api_keys: z.boolean(),
    model_cli_subscriptions: z.boolean(),
    tool_connections: z.boolean(),
    crew_mail_calendar: z.boolean(),
  })
  .meta({ id: "CompanySubscriptionFlags" });
export type CompanySubscriptionFlags = z.infer<typeof CompanySubscriptionFlags>;

/** What the caller may add here, and why a path is closed (platform switch or company flag). */
export const SubscriptionPolicy = z
  .object({
    platform: ModelCredentialPolicy,
    company: CompanySubscriptionFlags,
    effective: z.object({
      person_api_keys: z.boolean(),
      company_api_keys: z.boolean(),
      person_cli_subscriptions: z.boolean(),
      tool_connections: z.boolean(),
      /** Both company flags: tool connections and crew mail/calendar. */
      crew_mail_calendar: z.boolean(),
    }),
    /** The caller is an owner or admin: company-scope credentials, connections and the flags. */
    can_manage: z.boolean(),
  })
  .meta({ id: "SubscriptionPolicy" });
export type SubscriptionPolicy = z.infer<typeof SubscriptionPolicy>;

// ---- M4-04: mail and calendar for crews -----------------------------------------------------------

/** A crew may read its owner's mail or calendar through one of the owner's own connections. */
export const CrewConnectionGrant = z
  .object({
    company_id: Uuid,
    id: Uuid,
    crew_membership_id: Uuid,
    kind: CrewConnectionKind,
    connection_id: Uuid,
    granted_by_membership_id: Uuid,
    revoked_at: Timestamp.nullable(),
    ...ActorShortColumns.shape,
    ...stamps,
  })
  .meta({ id: "CrewConnectionGrant" });
export type CrewConnectionGrant = z.infer<typeof CrewConnectionGrant>;

/** One mail or calendar access: whether it is on, through which connection, and why not. */
export const CrewConnectionAccess = z
  .object({
    kind: CrewConnectionKind,
    grant: CrewConnectionGrant.nullable(),
    /** The owner's connections that can serve it (live, the owner's own, holding the tier). */
    eligible: z.array(
      z.object({
        connection_id: Uuid,
        label: z.string(),
        identity_display: z.string().nullable(),
        state: ToolConnectionState,
      }),
    ),
    /**
     * Why it cannot be on now: the company flag is off, no eligible connection, Gmail read is
     * waiting on Google's review (a founder gate), or the granted connection stopped serving.
     */
    blocked: z
      .enum(["flag_off", "no_connection", "founder_gated", "connection_unusable"])
      .nullable(),
  })
  .meta({ id: "CrewConnectionAccess" });
export type CrewConnectionAccess = z.infer<typeof CrewConnectionAccess>;

export const CrewConnections = z
  .object({ mail: CrewConnectionAccess, calendar: CrewConnectionAccess })
  .meta({ id: "CrewConnections" });
export type CrewConnections = z.infer<typeof CrewConnections>;

/** Google Calendar events, projected (Work OS `calendar_list`). */
export const CrewCalendarEvent = z
  .object({
    id: z.string().nullable(),
    summary: z.string().nullable(),
    status: z.string().nullable(),
    start: z.string().nullable(),
    end: z.string().nullable(),
    all_day: z.boolean(),
    location: z.string().nullable(),
    attendees: z.int().min(0),
    html_link: z.string().nullable(),
  })
  .meta({ id: "CrewCalendarEvent" });

export const CrewMailSummary = z
  .object({
    id: z.string().nullable(),
    thread_id: z.string().nullable(),
    from: z.string().nullable(),
    to: z.string().nullable(),
    subject: z.string().nullable(),
    date: z.string().nullable(),
    snippet: z.string().nullable(),
  })
  .meta({ id: "CrewMailSummary" });

export const CrewMailMessage = CrewMailSummary.extend({
  cc: z.string().nullable(),
  body: z.string(),
  attachments: z.array(z.object({ name: z.string(), size: z.int().min(0) })),
}).meta({ id: "CrewMailMessage" });
